// services/advanceChange.js — промяна на авансовите вноски по ЗКПО (чл. 88),
// ПРОМТ-22. Чиста функция: по новата прогнозна печалба и вече внесеното казва
// колко най-малко трябва да е новата вноска, за да няма лихва по чл. 89;
// с колко се внася точно данъкът; каква е лихвата без промяна; има ли
// надвнасяне. Екранът е src/calculators/advanceChange.js.
//
// Законът е сверен дословно в Ciela Net на 23.10.2026 (ДВ, бр. 97/23.10.2026):
//   • чл. 88, ал. 1 и 2 — декларация до 15 ноември; промяната важи СЛЕД
//     подаването;
//   • чл. 89, ал. 1, 2 и 4 — лихва върху А = Б − 1,25·В (месечни) или
//     А = 0,75·Б − 1,25·В (тримесечни); В са „определените" вноски — старите
//     до декларацията, новите след нея. Оттук: лихва няма при В ≥ 0,8·Б
//     (месечни) и В ≥ 0,6·Б (тримесечни);
//   • чл. 89, ал. 5 — лихвата е законната (ЗЛДТДПДВ, чл. 1) от 16 април до
//     31 декември; размерът ѝ (ЕЦБ + 8 пункта, 1/360 на ден — ПМС № 426/2014)
//     се въвежда, както в калкулатора „Законна лихва";
//   • чл. 90, ал. 1 и 2 — падежите (виж schedule()).

const { round2 } = require('./totals');
const holidays = require('./holidays');

// чл. 88, ал. 1 — „в срок до 15 ноември на съответната година".
const FILING_DEADLINE_MMDD = '11-15';
// чл. 89, ал. 5 — лихвата тече от 16 април до 31 декември.
const INTEREST_FROM_MMDD = '04-16';
const INTEREST_TO_MMDD = '12-31';
// ПМС № 426/2014 — дневният размер е 1/360 от годишния.
const DAY_BASIS = 360;

const MONTHS = ['януари', 'февруари', 'март', 'април', 'май', 'юни', 'юли', 'август', 'септември', 'октомври', 'ноември', 'декември'];
const eur = (n) => (Number(n) || 0).toFixed(2).replace('.', ',') + ' €';
const dfmt = (iso) => String(iso || '').split('-').reverse().join('.');

function workday(iso) {
  return holidays.isWorkingDay(iso) ? iso : holidays.nextWorkingDay(iso);
}

// Падежите по чл. 90. Месечни (ал. 1): януари–март до 15 април; април–ноември
// до 15-о число на месеца, „за който се отнасят"; декември до 1 декември.
// Тримесечни (ал. 2): I и II тримесечие до 15-о число на следващия месец,
// III — до 1 декември; за IV вноска няма. Неработен ден → следващ работен.
function schedule(kind, year) {
  const y = String(year);
  const raw = kind === 'monthly'
    ? MONTHS.map((name, i) => {
      const mm = String(i + 1).padStart(2, '0');
      const base = i < 3 ? y + '-04-15' : i < 11 ? y + '-' + mm + '-15' : y + '-12-01';
      return { period: name, dueBase: base };
    })
    : [
      { period: 'I тримесечие', dueBase: y + '-04-15' },
      { period: 'II тримесечие', dueBase: y + '-07-15' },
      { period: 'III тримесечие', dueBase: y + '-12-01' },
    ];
  return raw.map((x) => Object.assign(x, { due: workday(x.dueBase) }));
}

function daysInclusive(from, to) {
  return Math.round((Date.parse(to + 'T00:00:00Z') - Date.parse(from + 'T00:00:00Z')) / 86400000) + 1;
}

// Лихвата по чл. 89 за даден сбор от определените вноски V.
function interestFor(kind, B, V, annual, year) {
  const A = round2((kind === 'monthly' ? B : 0.75 * B) - 1.25 * V);
  const days = daysInclusive(year + '-' + INTEREST_FROM_MMDD, year + '-' + INTEREST_TO_MMDD);
  const interest = A > 0 ? round2(A * (Number(annual) || 0) / 100 * days / DAY_BASIS) : 0;
  return { V: round2(V), A, interest, days };
}

// Нагоре до цент — иначе от закръглянето надолу остава превишение от цент
// и с него лихва. Малкото „почистване" маха плаващата грешка (7407,4200001).
function ceilCent(x) {
  return Math.ceil(Math.round(x * 100 * 1e6) / 1e6) / 100;
}

function advanceChange(input) {
  const inp = input || {};
  const kind = inp.kind === 'monthly' ? 'monthly' : 'quarterly';
  const year = Number(inp.year) || Number(String(inp.filedOn || '').slice(0, 4)) || new Date().getFullYear();
  const declared = round2(Math.max(0, Number(inp.declared) || 0));
  const rate = inp.rate == null || inp.rate === '' ? 10 : Number(inp.rate);
  const filedOn = String(inp.filedOn || '').slice(0, 10);
  const notes = [];

  const B = round2((Number(inp.newProfit) || 0) * rate / 100);
  // Цел — сборът на вноските, с който се внася точно данъкът: при
  // тримесечните IV тримесечие няма вноска, затова е 0,75 × Б.
  const target = round2(kind === 'monthly' ? B : 0.75 * B);
  const noInterestShare = kind === 'monthly' ? 0.8 : 0.6;

  // След 15 ноември декларация по чл. 88 не се подава — сметката остава
  // „без промяна", а не тиха нова вноска, която законът не позволява.
  let error = null;
  const late = filedOn > year + '-' + FILING_DEADLINE_MMDD;
  if (late) error = 'Декларацията по чл. 88 се подава до 15 ноември (чл. 88, ал. 1 ЗКПО) — след тази дата вноските не могат да се променят.';

  // Вече подавана декларация през годината: нейната вноска важи от нейната
  // дата до сегашната (по контролния пример на промта — и за вноската с
  // падеж точно на онази дата).
  const prev = inp.changedAlready && inp.changedAlready.filedOn && Number(inp.changedAlready.amount) >= 0
    ? { filedOn: String(inp.changedAlready.filedOn).slice(0, 10), amount: round2(Number(inp.changedAlready.amount)) }
    : null;
  const usePrev = prev && prev.filedOn < filedOn;
  if (prev && !usePrev) notes.push('Предишната декларация трябва да е отпреди сегашната — не е взета предвид.');
  const current = usePrev ? prev.amount : declared;

  const sched = schedule(kind, year).map((x) => {
    // чл. 88, ал. 2: промяната важи след подаването → вноска с падеж до
    // датата на подаване (включително) остава „стара".
    const before = late || x.due <= filedOn;
    const amount = before ? (usePrev && x.due >= prev.filedOn ? prev.amount : declared) : null;
    return Object.assign({}, x, { phase: before ? 'before' : 'after', amount });
  });
  const nBefore = sched.filter((x) => x.phase === 'before').length;
  const nAfter = sched.length - nBefore;
  const paidBefore = round2(sched.filter((x) => x.phase === 'before').reduce((s, x) => s + x.amount, 0));

  // Без промяна остава последната декларирана вноска за оставащите падежи.
  const noChange = interestFor(kind, B, paidBefore + nAfter * current, inp.interestAnnual, year);

  let minNew = null;
  let exactNew = null;
  let overpaid = 0;
  let withMin = null;
  if (nAfter > 0) {
    if (paidBefore > target) {
      minNew = 0;
      exactNew = 0;
      overpaid = round2(paidBefore - target);
      notes.push('Вече внесеното надвишава очаквания данък с ' + eur(overpaid) + ' — надвнесеното се прихваща/възстановява с годишната декларация; новите вноски могат да са 0.');
    } else {
      minNew = ceilCent(Math.max(0, (noInterestShare * B - paidBefore) / nAfter));
      exactNew = ceilCent(Math.max(0, (target - paidBefore) / nAfter));
    }
    withMin = interestFor(kind, B, paidBefore + nAfter * minNew, inp.interestAnnual, year);
    sched.forEach((x) => { if (x.phase === 'after') x.amount = minNew; });
  } else {
    if (paidBefore > target) {
      overpaid = round2(paidBefore - target);
      notes.push('Вече внесеното надвишава очаквания данък с ' + eur(overpaid) + ' — надвнесеното се прихваща/възстановява с годишната декларация.');
    }
    notes.push('Няма вноска след датата на подаване — промяната вече не може да помогне; лихвата по чл. 89 е ' + eur(noChange.interest) + '.');
  }

  const shifted = sched.filter((x) => x.due !== x.dueBase);
  if (shifted.length) {
    notes.push('Падеж в неработен ден се мести на следващия работен: ' + shifted.map((x) => dfmt(x.dueBase) + ' → ' + dfmt(x.due)).join(', ') + '.');
  }
  if (noChange.interest > 0 && nAfter > 0) {
    notes.push('Лихвата е върху превишението над 25 % (' + eur(noChange.A) + ') за ' + noChange.days + ' дни от 16 април до 31 декември (чл. 89, ал. 5).');
  }
  notes.push('Печалбата от контролирано чуждестранно дружество и превишението по чл. 155а, ал. 1 и чл. 155б, ал. 1 не се броят в дължимия данък за лихвата (чл. 91а ЗКПО) — ако ги има, извадете ги от прогнозата.');

  return {
    kind, year, B, target, rate, declared, filedOn, error,
    schedule: sched, nBefore, nAfter, paidBefore,
    minNew, exactNew, noChange, withMin, overpaid, notes,
  };
}

module.exports = { advanceChange, schedule, interestFor, FILING_DEADLINE_MMDD };
